/**
 * Reconnect backoff schedule.
 *
 * Exponential 1s → 2s → 4s → 8s → 16s → 32s → 64s → 128s → 256s → 300s (capped).
 * The 5-minute ceiling exists so chronic failures (revoked refresh token, network
 * down, authorizer rejecting) don't poll hot every 30s. After ~9 attempts the
 * cap kicks in. reset() rewinds to the start — call after a successful connection
 * so the next disconnect starts fresh rather than at the last failed delay.
 */

export interface ReconnectBackoffOptions {
   initialDelayMs?: number
   maxDelayMs?: number
   multiplier?: number
}

export default class ReconnectBackoff {
   private attempts = 0
   private readonly initialDelayMs: number
   private readonly maxDelayMs: number
   private readonly multiplier: number

   constructor(opts: ReconnectBackoffOptions = {}) {
      this.initialDelayMs = opts.initialDelayMs ?? 1000
      this.maxDelayMs = opts.maxDelayMs ?? 300_000
      this.multiplier = opts.multiplier ?? 2
   }

   nextDelay = (): number => {
      const delay = Math.min(this.initialDelayMs * this.multiplier ** this.attempts, this.maxDelayMs)
      this.attempts++
      return delay
   }

   reset = () => {
      this.attempts = 0
   }

   attemptCount = (): number => this.attempts
}
